import { utc } from '@date-fns/utc';
import { differenceInHours, format, parseISO, subMonths } from 'date-fns';

export function skeletonItems(count: number, prefix?: string) {
  return Array.from({ length: count }, (_, i) => ({ id: prefix ? `${prefix}-${i}` : `${i}` }));
}

/**
 * Escape a string for use inside a `RegExp`. `RegExp.escape` does this natively but only from
 * Safari 18.2 and Chrome 136, which is past what the browser bundles target.
 */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type InitialsType = 'all' | 'first';
export function getInitials(value: string, type: InitialsType = 'all') {
  return value
    .split(/[\s@]+/)
    .filter((p) => /^[a-zA-Z0-9]/.test(p))
    .map((p) => p[0])
    .slice(0, type === 'first' ? 1 : 2)
    .join('')
    .toUpperCase();
}

export function truncateMiddle(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;

  const keep = maxLength - 3;
  const start = Math.ceil(keep / 2);
  const end = Math.floor(keep / 2);
  return `${value.slice(0, start)}...${value.slice(value.length - end)}`;
}

export function hoursDiff(start: Date, end: Date) {
  return Math.abs(differenceInHours(end, start, { roundingMethod: 'round' }));
}

export function parseParagraphs(text: string): string[] {
  return text
    .replace(/([.!?])\n([A-Z])/g, '$1\n\n$2') // sentence-ending newline → paragraph break
    .split(/\n\n+/)
    .map((p) => p.replace(/\n/g, ' ').trim()) // remaining newlines are PDF soft-wraps → space
    .filter(Boolean);
}

/** The current month as `YYYY-MM`, in UTC so it does not depend on where the code runs. */
export function currentMonth(): string {
  return format(new Date(), 'yyyy-MM', { in: utc });
}

/** The `YYYY-MM` month before another. */
export function previousMonth(month: string): string {
  return format(subMonths(monthStart(month), 1), 'yyyy-MM');
}

/**
 * The first day of a `YYYY-MM` month at local midnight, for formatting. Reading the string as UTC
 * instead would show the month before anywhere west of Greenwich.
 */
export function monthStart(month: string): Date {
  return parseISO(month);
}
